<template>
  <div>
    <app-cells>
      <h2 class="title">Account &amp; Personal Cabinet</h2>
    </app-cells>
    <app-page-container class="personal-cabinet" custom="625px">
      <app-spinner v-if="is_loading" />
      <div v-else-if="load_error" class="personal-cabinet__error-state">
        <div>{{ load_error }}</div>
        <app-button size="small" theme="warn" @click.native="fetchState">Retry</app-button>
      </div>
      <template v-else-if="access_state">
        <div class="personal-cabinet__body">
          <div class="personal-cabinet__row">
            <span
              class="personal-cabinet__indicator"
              :class="{ 'personal-cabinet__indicator--active': access_state.account.exists }"
            ></span>
            <div>
              <div class="personal-cabinet__label">User account</div>
              <div class="personal-cabinet__value">
                {{ access_state.account.exists ? access_state.account.username : 'Not created' }}
              </div>
            </div>
            <div class="personal-cabinet__action">
              <span
                v-if="access_state.account.exists"
                class="personal-cabinet__status"
                :class="{ 'personal-cabinet__status--inactive': !access_state.account.is_active }"
              >
                {{ access_state.account.is_active ? 'Active' : 'Inactive' }}
              </span>
              <app-button
                v-else-if="can_manage_personal_cabinet"
                size="small"
                :disabled="!is_employee_working || action_loading"
                @click.native="showCreateModal"
              >
                Create user
              </app-button>
            </div>
          </div>
          <div class="personal-cabinet__row">
            <span
              class="personal-cabinet__indicator"
              :class="{ 'personal-cabinet__indicator--active': access_state.personal_cabinet.enabled }"
            ></span>
            <div>
              <div class="personal-cabinet__label">Personal Cabinet</div>
              <div class="personal-cabinet__value">
                Access {{ access_state.personal_cabinet.enabled ? 'enabled' : 'disabled' }}
              </div>
            </div>
            <div class="personal-cabinet__action">
              <app-switcher
                :value="access_state.personal_cabinet.enabled"
                :readonly="!can_manage_access"
                @click.native.prevent="onAccessSwitcherClick"
              />
            </div>
          </div>
          <div v-if="blocked_message" class="personal-cabinet__blocked">
            {{ blocked_message }}
          </div>
        </div>
      </template>
    </app-page-container>

    <modal
      name="personal-cabinet-create-user"
      :width="540"
      :height="'auto'"
      :adaptive="true"
      :click-to-close="false"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="closeCreateModal"></button>
        <div class="popup__title">Create user account</div>
        <div class="popup__body">
          <div class="popup__subt">
            A User account will be created and linked to this employee.
          </div>
          <div class="personal-cabinet__create-option">
            <app-checkbox
              v-model="create_personal_cabinet"
              label="Create Personal Cabinet access"
            />
          </div>
          <div v-if="action_error" class="personal-cabinet__modal-error">{{ action_error }}</div>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" :disabled="action_loading" @click.native="closeCreateModal">
              Cancel
            </app-button>
            <app-button
              ref="createUserSubmit"
              :disabled="action_loading"
              @click.native="createUser"
            >
              Confirm
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>

    <modal
      name="personal-cabinet-access-action"
      :width="500"
      :height="'auto'"
      :adaptive="true"
      :click-to-close="false"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="closeAccessModal"></button>
        <div class="popup__title">{{ access_action === 'enable' ? 'Enable' : 'Disable' }} Personal Cabinet</div>
        <div class="popup__body">
          <div class="popup__subt">{{ access_confirmation }}</div>
          <div v-if="action_error" class="personal-cabinet__modal-error">{{ action_error }}</div>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" :disabled="action_loading" @click.native="closeAccessModal">
              Cancel
            </app-button>
            <app-button
              ref="accessActionSubmit"
              :theme="access_action === 'disable' ? 'error' : 'default'"
              :disabled="action_loading"
              @click.native="confirmAccessAction"
            >
              Confirm
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>

    <modal
      name="personal-cabinet-credentials"
      :width="540"
      :height="'auto'"
      :adaptive="true"
      :click-to-close="false"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="closeCredentialsModal"></button>
        <div class="popup__title">User account created</div>
        <div v-if="credentials" class="popup__body">
          <div class="personal-cabinet__credentials-note">
            The employee will receive an email with a sign-in link and their account credentials.
          </div>
          <app-cells :indent="false" position="center">
            <app-button @click.native="closeCredentialsModal">Done</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getEmployeePersonalCabinetAccess,
  postEmployeePersonalCabinetCreateUser,
  postEmployeePersonalCabinetDisable,
  postEmployeePersonalCabinetEnable,
} from '@/http'

export default {
  name: 'PersonalCabinetAccess',
  props: {
    employeeId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      access_state: null,
      is_loading: false,
      load_error: '',
      action_loading: false,
      action_error: '',
      create_personal_cabinet: true,
      access_action: '',
      credentials: null,
    }
  },
  computed: {
    can_manage_personal_cabinet() {
      return this.$store.state.userGroups.some(role => (
        role.code === 'hr_manager' ||
        role.code === 'hr_supervisor' ||
        role.code === 'general_manager'
      ))
    },
    is_employee_working() {
      return Boolean(
        this.access_state &&
        this.access_state.employee.employment_status &&
        this.access_state.employee.employment_status.code === 'working'
      )
    },
    can_enable_access() {
      return Boolean(
        this.access_state.account.is_active &&
        this.is_employee_working &&
        !this.access_state.personal_cabinet.blocked_reason
      )
    },
    can_manage_access() {
      if (!this.can_manage_personal_cabinet || this.action_loading || !this.access_state.account.exists) return false
      if (this.access_state.personal_cabinet.enabled) return true
      return this.can_enable_access
    },
    blocked_message() {
      if (!this.access_state || !this.access_state.personal_cabinet.blocked_reason) return ''

      const messages = {
        employee_not_working: 'Personal Cabinet cannot be enabled because the employee is not working.',
        termination_date_reached: 'Personal Cabinet cannot be enabled because the termination date has been reached.',
        user_inactive: 'Personal Cabinet cannot be enabled because the User account is inactive.',
      }
      return messages[this.access_state.personal_cabinet.blocked_reason] || 'Personal Cabinet access is blocked.'
    },
    access_confirmation() {
      if (this.access_action === 'enable') return 'Enable Personal Cabinet for this employee?'
      return 'The employee will immediately lose access to Personal Cabinet. The User account and other roles will remain unchanged.'
    },
  },
  created() {
    this.fetchState()
  },
  methods: {
    fetchState(showLoader = true) {
      if (showLoader) {
        this.is_loading = true
        this.load_error = ''
      }
      return getEmployeePersonalCabinetAccess(this.employeeId)
        .then(response => {
          this.access_state = response.data
          this.$emit('state-change', this.access_state)
        })
        .catch(() => {
          if (showLoader) this.load_error = 'Unable to load Account & Personal Cabinet state.'
        })
        .finally(() => {
          if (showLoader) this.is_loading = false
        })
    },
    showCreateModal() {
      if (this.action_loading) return
      this.create_personal_cabinet = true
      this.action_error = ''
      this.$modal.show('personal-cabinet-create-user')
    },
    closeCreateModal() {
      if (this.action_loading) return
      this.$modal.hide('personal-cabinet-create-user')
    },
    createUser() {
      if (this.action_loading) return
      this.action_loading = true
      this.action_error = ''
      this.$refs.createUserSubmit.preload = true

      postEmployeePersonalCabinetCreateUser(this.employeeId, {
        create_personal_cabinet: this.create_personal_cabinet,
      })
        .then(response => {
          this.access_state = this.withoutCredentials(response.data)
          this.credentials = response.data.credentials
          this.$modal.hide('personal-cabinet-create-user')
          this.$notify({ type: 'success', title: 'User account created' })
          this.$nextTick(() => this.$modal.show('personal-cabinet-credentials'))
        })
        .catch(error => {
          this.action_error = this.getErrorMessage(error)
        })
        .finally(() => {
          this.action_loading = false
          this.$refs.createUserSubmit.preload = false
        })
    },
    showAccessModal(action) {
      if (this.action_loading) return
      this.access_action = action
      this.action_error = ''
      this.$modal.show('personal-cabinet-access-action')
    },
    onAccessSwitcherClick() {
      if (!this.can_manage_access) return
      const action = this.access_state.personal_cabinet.enabled ? 'disable' : 'enable'
      this.showAccessModal(action)
    },
    closeAccessModal() {
      if (this.action_loading) return
      this.$modal.hide('personal-cabinet-access-action')
    },
    confirmAccessAction() {
      if (this.action_loading || !this.access_action) return
      this.action_loading = true
      this.action_error = ''
      this.$refs.accessActionSubmit.preload = true

      const requests = {
        enable: postEmployeePersonalCabinetEnable,
        disable: postEmployeePersonalCabinetDisable,
      }
      const successTitles = {
        enable: 'Personal Cabinet access enabled',
        disable: 'Personal Cabinet access disabled',
      }
      const request = requests[this.access_action](this.employeeId)

      request
        .then(response => {
          this.access_state = response.data
          return this.fetchState(false)
        })
        .then(() => {
          this.$modal.hide('personal-cabinet-access-action')
          this.$notify({ type: 'success', title: successTitles[this.access_action] })
        })
        .catch(error => {
          this.action_error = this.getErrorMessage(error)
        })
        .finally(() => {
          this.action_loading = false
          this.$refs.accessActionSubmit.preload = false
        })
    },
    closeCredentialsModal() {
      this.$modal.hide('personal-cabinet-credentials')
      this.credentials = null
    },
    withoutCredentials(data) {
      const state = { ...data }
      delete state.credentials
      return state
    },
    getErrorMessage(error) {
      const data = error && error.response && error.response.data
      if (!data) return 'Unable to complete the request. Please try again.'
      if (typeof data === 'string') return data

      const firstError = Object.values(data)[0]
      if (Array.isArray(firstError)) return firstError[0]
      if (typeof firstError === 'string') return firstError
      return 'Unable to complete the request. Please try again.'
    },
  }
}
</script>

<style lang="sass" scoped>
.personal-cabinet
  margin-bottom: 30px
  border: 1px solid $color-border
  border-radius: 12px
  background-color: #fff
  overflow: hidden

.personal-cabinet__body
  padding: 0 22px

.personal-cabinet__row
  display: grid
  grid-template-columns: 16px minmax(0, 1fr) auto
  align-items: center
  gap: 12px
  min-height: 82px
  border-bottom: 1px solid #e8edf1

  &:last-of-type
    border-bottom: 0

.personal-cabinet__indicator
  display: flex
  align-items: center
  justify-content: center
  width: 14px
  height: 14px
  border-radius: 50%
  background-color: #dfe5e9

.personal-cabinet__indicator--active
  background-color: $color-theme

.personal-cabinet__label
  margin-bottom: 3px
  color: $color-text-base
  font-size: 16px
  font-weight: 600

.personal-cabinet__value
  color: $color-text-second
  font-size: 14px
  overflow-wrap: anywhere

.personal-cabinet__action
  display: flex
  align-items: center
  justify-content: flex-end

.personal-cabinet__status
  padding: 6px 12px
  border-radius: 20px
  background-color: rgba($color-theme, 0.14)
  color: $color-theme
  font-size: 13px
  font-weight: 600

.personal-cabinet__status--inactive
  background-color: rgba($color-error, 0.12)
  color: $color-error

.personal-cabinet__blocked
  margin: 14px 0 18px
  padding: 12px 14px
  border-radius: 7px
  background-color: rgba($color-error, 0.08)
  color: $color-error
  font-size: 13px
  line-height: 1.45

.personal-cabinet__error-state
  display: flex
  align-items: center
  justify-content: center
  flex-direction: column
  gap: 14px
  min-height: 150px
  padding: 20px
  color: $color-text-second
  text-align: center

.personal-cabinet__create-option
  margin: 20px 0

.personal-cabinet__modal-error
  margin-bottom: 15px
  color: $color-error
  font-size: 13px
  overflow-wrap: anywhere

.personal-cabinet__credentials-note
  margin-bottom: 18px
  color: $color-text-second
  line-height: 1.45

.personal-cabinet__credential
  display: flex
  align-items: center
  justify-content: space-between
  gap: 15px
  margin-bottom: 14px
  padding: 14px 16px
  border: 1px solid $color-border
  border-radius: 8px

.personal-cabinet__credential-label
  margin-bottom: 4px
  color: $color-text-second
  font-size: 13px

.personal-cabinet__credential-value
  color: $color-text-base
  font-size: 17px
  font-weight: 600
  overflow-wrap: anywhere

@media (max-width: 600px)
  .personal-cabinet__row
    grid-template-columns: 16px minmax(0, 1fr)
    padding: 15px 0

  .personal-cabinet__action
    grid-column: 2
    justify-content: flex-start

  .personal-cabinet__credential
    align-items: stretch
    flex-direction: column
</style>
